// information tab
import React, { useEffect, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { flowRight, get, isEmpty, forEach } from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { ModalContext } from 'App';
import { getJson } from 'session';
import { Acl } from 'modules/common/components/Acl';
import ViewTabImage from 'modules/common/components/ViewTabImage';
import TransactionInformation from '../component/TransactionInformation';
import VehicleInformation from '../component/VehicleInformation';
import VerifyReceipt from '../component/VerifyReceipt';
import ViewPermitModal from '../modals/ViewPermitModal';
import GeneratePermitModal from '../modals/GeneratePermitModal';
import * as actions from '../action';
import * as c from '../constant';
import UseStartAssessment from '../component/UseStartAssessment';
import AddSubmitAdminRemarksModal from '../modals/AddSubmitAdminRemarksModal';
import SpecialPermitUpdateFeesModal from '../modals/SpecialPermitUpdateFeesModal';
import AddSubmitTreasuryRemarksModal from '../modals/AddSubmitTreasuryRemarksModal';
import AddFinalizeRemarksModal from '../modals/AddFinalizeRemarksModal';
import AddPrintedRemarksModal from '../modals/AddPrintedRemarksModal';
import AddReceivedRemarksModal from '../modals/AddReceivedRemarksModal';

function ApplicationContent({
  transactionInfo,
  showTransaction,
  clearTransaction,
  isLoading,
  match,
  taxpayer,
  toggleAssessment,
  tagVehicleUnit,
  assisting,
}) {
  const setModal = useContext(ModalContext);
  const selectedTransaction = match.params.id;
  const permissions = getJson('permissions');

  const isStartAssessment = get(transactionInfo, 'included.transaction_history.attributes.is_start_assessment');

  const showVerifyReceipt = (status) => {
    return [
      'WAITING FOR PAYMENT',
      'PAID',
      'FOR PICK-UP',
      'PENDING',
      'FOR ASSESSMENT (ADMIN)',
      'FOR ASSESSMENT (TREASURY)',
      'FINALIZED',
      'EXPIRED REFERENCE NUMBER',
    ].includes(status);
  };

  const updateData = () => {
    showTransaction(selectedTransaction);
  };

  useEffect(() => {
    if (!isEmpty(selectedTransaction)) {
      updateData();
    }
    return () => {
      clearTransaction();
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(selectedTransaction)) {
      updateData();
    }
  }, [selectedTransaction]);

  const getInitials = (cString) => {
    if (cString) {
      const val = cString.replace(/\[^a-zA-Z- ]/g, '').match(/\b(\w)/g);
      const strFirstTwo = val.slice(0, 2);
      const final = strFirstTwo.join('');
      return final.toUpperCase();
    }
    return null;
  };

  const handleVerifyReceipt = (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <VerifyReceipt
          transactionInfo={transactionInfo}
          id={get(transactionInfo, 'included.transaction_history.id')}
        />
      ),
      title: `Assess Payment Receipt`,
      modalSize: 'modal-md',
    });
  };

  const handleViewPermit = (e) => {
    e.preventDefault();

    setModal({
      isOpen: true,
      content: (
        <ViewPermitModal data={transactionInfo} id={selectedTransaction} />
      ),
      title: 'View Permit',
      modalSize: 'modal-xl',
    });
  };

  const handleGeneratePermit = (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <GeneratePermitModal data={transactionInfo} id={selectedTransaction} />
      ),
      title: 'Generate Permit',
      modalSize: 'modal-md',
    });
  };

  const handleTagUnit = (id, tag) => (e) => {
    e.preventDefault();
    const args = {
      'validity': tag
    }
    tagVehicleUnit(id, args, () => {
      showTransaction(get(transactionInfo, 'id'));
    })
  }

  const isEmployed = get(taxpayer, 'is_employed_individual');
  const transactionHistory = get(transactionInfo, 'included.transaction_history')
  const tStatus = get(transactionHistory, 'attributes.transaction_status');
  const isPermitGenerated = get(transactionInfo, 'attributes.permit_generated_at');
  const isTricycle = get(transactionInfo, "attributes.type") === "TRICYCLE"
  const isVehicleValidated = () => {
    let isValid = false
    forEach(get(transactionInfo, 'included.vehicles'), ((x) => forEach(x.attributes.vehicle_unit, ((y) => { isValid = isValid || y.validity === 'PENDING' }))))
    return !isValid
  }

  const startAssessment = UseStartAssessment(
    assisting,
    isStartAssessment,
    get(transactionInfo, 'included.transaction_history.attributes.transaction_status'),
    () => {
      toggleAssessment(get(transactionInfo, 'included.transaction_history.id'), {}, () => {
        showTransaction(get(transactionInfo, 'id'));
      });
    }
  );

  const handleSubmitToAdmin = e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <AddSubmitAdminRemarksModal
          transactionId={selectedTransaction}
          id={get(transactionInfo, 'included.transaction_history.id')}
          isVehicleValidated={isVehicleValidated}
        />
      ),
      title: `Confirmation`,
      modalSize: 'modal-md',
    });
  };

  const handleSubmitToTreasury = e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <AddSubmitTreasuryRemarksModal
          transactionId={selectedTransaction}
          id={get(transactionInfo, 'included.transaction_history.id')}
        />
      ),
      title: `Confirmation`,
      modalSize: 'modal-md',
    });
  };

  const handleFinalize = e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <AddFinalizeRemarksModal
          transactionId={selectedTransaction}
          id={get(transactionInfo, 'included.transaction_history.id')}
        />
      ),
      title: `Confirmation`,
      modalSize: 'modal-md',
    });
  };

  const handleOnShowUpdateFees = data => e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <SpecialPermitUpdateFeesModal
          transactionId={selectedTransaction}
          data={data}
        />
      ),
      title: `Update Fees`,
      modalSize: 'modal-md',
    });
  };

  const handleTagAsPrinted = (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <AddPrintedRemarksModal
          transactionId={selectedTransaction}
          id={get(transactionInfo, 'included.transaction_history.id')}
        />
      ),
      title: `Confirmation`,
      modalSize: 'modal-md',
    });
  };

  const handleTagAsReceived = (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <AddReceivedRemarksModal
          transactionId={selectedTransaction}
          id={get(transactionInfo, 'included.transaction_history.id')}
        />
      ),
      title: `Confirmation`,
      modalSize: 'modal-md',
    });
  };

  return (
    <>
      <div className="card-section-content container-fluid">
        {isLoading && (
          <div className="loading">
            <div className="spinner text-center">
              <span className="fas fa-1x fa-spin fa-spinner" />
            </div>
          </div>
        )}
        <div className="right-section-content">
          {/* GRAY HIGHLIGHT */}
          <div className="row highlight d-flex align-items-center mx-3 py-3">
            <div className="col-12 px-0 mt-0 mb-1">
              {/* BORDER */}
              <div className="card border-primary">
                <div className="container-fluid py-1">
                  <div className="p-0 d-flex flex-nowrap mb-3 w-100">
                    <div className="d-flex round-image justify-content-center">
                      <h3 className="d-flex align-items-center initials mb-0">
                        {getInitials(
                          `${get(taxpayer, 'first_name')} ${get(
                            taxpayer,
                            'middle_name'
                          )} ${get(taxpayer, 'last_name')}`
                        )}
                      </h3>
                    </div>
                    <div className="d-flex ml-4 align-items-center">
                      <h1 className="transaction-company m-0">
                        {`${get(taxpayer, 'first_name') || '-'} ${get(taxpayer, 'middle_name') || ''
                          } ${get(taxpayer, 'last_name') || ''} ${get(taxpayer, 'extension_name') || ''
                          }`}
                      </h1>
                    </div>
                  </div>
                  <div className="d-flex ml-auto justify-content-end mb-3">
                    {isStartAssessment === 1 &&
                      (Acl(permissions, ['SPECIAL PERMIT SUBMIT TO ADMIN']))
                      &&
                      (tStatus === 'FOR CHECKING')
                      && (
                        <button
                          type="button"
                          className="btn gen-btn font-weight-bold btn-primary cms-bg-color-primary cms-outline-primary mr-2"
                          onClick={handleSubmitToAdmin}
                        >
                          Submit to Admin
                        </button>
                      )
                    }

                    {isStartAssessment === 1 &&
                      (Acl(permissions, ['SPECIAL PERMIT SUBMIT TO TREASURY']))
                      && (tStatus === 'FOR ASSESSMENT (ADMIN)')
                      && (
                        <button
                          type="button"
                          className="btn gen-btn font-weight-bold btn-primary cms-bg-color-primary cms-outline-primary mr-2"
                          onClick={handleSubmitToTreasury}
                        >
                          Submit to Treasury
                        </button>
                      )
                    }

                    {isStartAssessment === 1 &&
                      (Acl(permissions, ['SPECIAL PERMIT FINALIZE']))
                      && (tStatus === 'FOR ASSESSMENT (TREASURY)')
                      && (
                        <button
                          type="button"
                          className="btn gen-btn font-weight-bold btn-primary cms-bg-color-primary cms-outline-primary mr-2"
                          onClick={handleFinalize}
                        >
                          Finalize Application
                        </button>
                      )
                    }

                    {isStartAssessment === 1 &&
                      ((tStatus === 'FOR ASSESSMENT (ADMIN)') ||
                        (tStatus === 'FOR ASSESSMENT (TREASURY)')
                      )
                      && (
                        <button
                          type="button"
                          className="btn gen-btn font-weight-bold btn-primary cms-bg-color-primary cms-outline-primary mr-2"
                          onClick={handleOnShowUpdateFees(transactionInfo)}
                        >
                          Update Fees
                        </button>
                      )
                    }

                    {(Acl(permissions, ['START ASSESSMENT SPECIAL PERMIT']))
                      &&
                      startAssessment.component
                    }

                    {showVerifyReceipt(tStatus) ? (
                      <button
                        type="button"
                        className="btn gen-btn font-weight-bold btn-primary cms-bg-color-primary cms-outline-primary ml-2"
                        onClick={handleVerifyReceipt}
                      >
                        Assess Payment Receipt
                      </button>
                    ) : (
                      ''
                    )}

                    {tStatus === 'PRINTED'
                      && (Acl(permissions, ['SPECIAL PERMIT TAG AS RECEIVED']))
                      ? (
                        <button
                          type="button"
                          className="btn gen-btn font-weight-bold btn-primary cms-bg-color-primary cms-outline-primary mr-2"
                          onClick={handleTagAsReceived}
                        >
                          Tag as Received
                        </button>
                      ) : (
                        ''
                      )}
                    {tStatus === 'FOR PICK-UP' && isPermitGenerated &&
                      (Acl(permissions, ['SPECIAL PERMIT TAG AS PRINTED']))
                      ? (
                        <button
                          type="button"
                          className="ml-2 btn gen-btn font-weight-bold btn-primary cms-bg-color-primary cms-outline-primary mr-2"
                          onClick={handleTagAsPrinted}
                        >
                          Tag as Printed
                        </button>
                      ) : (
                        ''
                      )}
                    &nbsp;

                    {Acl(permissions, ['SPECIAL PERMIT VIEW PERMIT']) &&
                      isPermitGenerated ?
                      (
                        <button
                          type="button"
                          className="btn gen-btn font-weight-bold btn-primary cms-bg-color-primary cms-outline-primary ml-2"
                          onClick={handleViewPermit}
                        >
                          View Permit
                        </button>
                      ) : (
                        ''
                      )}
                    {Acl(permissions, ['SPECIAL PERMIT GENERATE PERMIT']) &&
                      !isPermitGenerated && tStatus === 'FOR PICK-UP' ?
                      (
                        <button
                          type="button"
                          className="btn gen-btn font-weight-bold btn-primary cms-bg-color-primary cms-outline-primary ml-2"
                          onClick={handleGeneratePermit}
                        >
                          Generate Permit
                        </button>
                      ) : (
                        ''
                      )}
                  </div>
                  <Tabs>
                    <TabList>
                      <Tab>Transaction</Tab>
                      <Tab>Information</Tab>
                      <Tab>Vehicles</Tab>
                      <Tab>Requirements</Tab>
                      <Tab>Fees</Tab>
                    </TabList>
                    <TabPanel>
                      <TransactionInformation
                        module="sp"
                        transactionInfo={transactionInfo}
                        taxpayer={taxpayer}
                      />
                    </TabPanel>
                    <TabPanel>
                      <div className="col-12 p-3">
                        <table className="transaction-table table table-borderless table-sm ">
                          <tbody>
                            <tr>
                              <td><p className="mb-2" style={{ fontSize: '16px' }}>Owner Information</p></td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">Last Name</td>
                              <td className="text-dark" width="1">:</td>
                              <td>
                                <b>{(get(taxpayer, 'last_name') || '--').toUpperCase()}</b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">Middle Name</td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {(
                                    get(taxpayer, 'middle_name') || '--'
                                  ).toUpperCase()}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                First Name
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {(
                                    get(taxpayer, 'first_name') || '--'
                                  ).toUpperCase()}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Extension Name
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {(
                                    get(taxpayer, 'extension_name') || '--'
                                  ).toUpperCase()}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Address
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {get(taxpayer, 'region_name') || '--'}
                                  {''}
                                  {get(taxpayer, 'province_name') ||
                                    '--'}{' '}
                                  {get(taxpayer, 'municipality_name') || '--'}{' '}
                                  {get(taxpayer, 'barangay_name') || '--'}{' '}
                                  {get(taxpayer, 'street') || '--'}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Is Employed
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>{isEmployed ? 'YES' : 'NO'}</b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Gender
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {(
                                    get(taxpayer, 'gender') || '--'
                                  ).toUpperCase()}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Civil Status
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {(
                                    get(taxpayer, 'civil_status') || '--'
                                  ).toUpperCase()}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Place of Birth
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {(
                                    get(
                                      taxpayer,
                                      'place_of_birth_region_name'
                                    ) || '--'
                                  ).toUpperCase()}{' '}
                                  {(
                                    get(
                                      taxpayer,
                                      'place_of_birth_province_name'
                                    ) || '--'
                                  ).toUpperCase()}{' '}
                                  {(
                                    get(
                                      taxpayer,
                                      'place_of_birth_municipality_name'
                                    ) || '--'
                                  ).toUpperCase()}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Date of Birth
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>{get(taxpayer, 'birth_date') || '--'}</b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Height
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {(
                                    get(taxpayer, 'height') || '--'
                                  ).toUpperCase()}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Weight
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {(
                                    get(taxpayer, 'weight') || '--'
                                  ).toUpperCase()}
                                </b>
                              </td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">
                                Residence Tax Due
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td>
                                <b>
                                  {get(taxpayer, 'residence_tax_due') || '--'}
                                </b>
                              </td>
                            </tr>

                            {
                              isTricycle ? (
                                <>
                                  {/* Tricycle Information */}
                                  <tr>
                                    <td><p className="mb-2 mt-3 w-100" style={{ fontSize: '16px' }}>Driver and Tricycle Information</p></td>
                                  </tr>
                                  <tr>
                                    <td className="text-dark" width="250">Last Name</td>
                                    <td className="text-dark" width="1">:</td>
                                    <td><b>{get(transactionInfo, "attributes.driver_last_name") || '--'}</b></td>
                                  </tr>
                                  <tr>
                                    <td className="text-dark" width="250">First Name</td>
                                    <td className="text-dark" width="1">:</td>
                                    <td><b>{get(transactionInfo, 'attributes.driver_first_name') || '--'}</b></td>
                                  </tr>
                                  <tr>
                                    <td className="text-dark" width="250">Extension Name</td>
                                    <td className="text-dark" width="1">:</td>
                                    <td><b>{get(transactionInfo, 'attributes.driver_middle_name') || '--'}</b></td>
                                  </tr>
                                  <tr>
                                    <td className="text-dark" width="250">Address</td>
                                    <td className="text-dark" width="1">:</td>
                                    <td>
                                      <b>
                                        {get(transactionInfo, 'attributes.driver_region_name') || '--'}
                                        {''}
                                        {get(transactionInfo, 'attributes.driver_province_name') ||
                                          '--'}{' '}
                                        {get(transactionInfo, 'attributes.driver_municipality_name') || '--'}{' '}
                                        {get(transactionInfo, 'attributes.driver_barangay_name') || '--'}{' '}
                                        {get(transactionInfo, 'attributes.driver_street') || '--'}
                                      </b>
                                    </td>
                                  </tr>
                                </>
                              ) : (
                                <>
                                  {/* PUV Information */}
                                  <tr>
                                    <td><p className="mb-2 mt-3 w-100" style={{ fontSize: '16px' }}>Registrant Information</p></td>
                                  </tr>
                                  <tr>
                                    <td className="text-dark" width="250">Applicant</td>
                                    <td className="text-dark" width="1">:</td>
                                    <td><b>{get(transactionInfo, "attributes.applicant_name") || '--'}</b></td>
                                  </tr>
                                  <tr>
                                    <td className="text-dark" width="250">Bus Name</td>
                                    <td className="text-dark" width="1">:</td>
                                    <td><b>{get(transactionInfo, "attributes.bus_name") || '--'}</b></td>
                                  </tr>
                                  <tr>
                                    <td className="text-dark" width="250">Ownership</td>
                                    <td className="text-dark" width="1">:</td>
                                    <td><b>{get(transactionInfo, "attributes.ownership") || '--'}</b></td>
                                  </tr>
                                  <tr>
                                    <td className="text-dark" width="250">Address</td>
                                    <td className="text-dark" width="1">:</td>
                                    <td>
                                      <b>
                                        {get(transactionInfo, 'attributes.applicant_region_name') || '--'}
                                        {''}
                                        {get(transactionInfo, 'attributes.applicant_province_name') ||
                                          '--'}{' '}
                                        {get(transactionInfo, 'attributes.applicant_municipality_name') || '--'}{' '}
                                        {get(transactionInfo, 'attributes.applicant_barangay_name') || '--'}{' '}
                                        {get(transactionInfo, 'attributes.applicant_street') || '--'}
                                      </b>
                                    </td>
                                  </tr>
                                </>
                              )
                            }

                            <tr>
                              <td><p className="mb-2 mt-3" style={{ fontSize: '16px' }}>Permit Information</p></td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">Permit Type</td>
                              <td className="text-dark" width="1">:</td>
                              <td><b>{get(transactionInfo, 'attributes.type') || '--'}</b></td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">Permit Validity</td>
                              <td className="text-dark" width="1">:</td>
                              <td><b>{get(transactionInfo, 'attributes.permit_validity') || '--'}</b></td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">Status</td>
                              <td className="text-dark" width="1">:</td>
                              <td><b>{get(transactionInfo, 'attributes.status') || '--'}</b></td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">Permit No.</td>
                              <td className="text-dark" width="1">:</td>
                              <td><b>{get(transactionInfo, 'attributes.permit_no') || '--'}</b></td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">Business Plate No.</td>
                              <td className="text-dark" width="1">:</td>
                              <td><b>{get(transactionInfo, `attributes.${isTricycle ? "plate_no" : "bus_plate_number"}`) || '--'}</b></td>
                            </tr>
                            <tr>
                              <td className="text-dark" width="250">Piscal Year</td>
                              <td className="text-dark" width="1">:</td>
                              <td><b>{get(transactionInfo, 'attributes.fiscal_year') || '--'}</b></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </TabPanel>
                    <TabPanel>
                      <VehicleInformation
                        transactionInfo={transactionInfo}
                        handleTagUnit={handleTagUnit}
                      />
                    </TabPanel>
                    <TabPanel>
                      <div className=" p-3">
                        <ViewTabImage
                          label="Requirements"
                          data={(
                            get(
                              transactionInfo,
                              'included.requirement_files'
                            ) || []
                          ).map(x => ({
                            ...x,
                            id: x.id,
                            name: x.attributes._application_requirement,
                            content: x.attributes.url,
                          }))}
                        />
                      </div>
                    </TabPanel>
                    <TabPanel>
                      <div className="col-12 p-3">
                        <p className="mb-2 p-1 font-weight-bold cms-text-primary" style={{ fontSize: '16px' }}>Fees</p>
                        <div className="mb-3 p-1 form-wrapper-alternate-bg">
                          <InputDisplayTemplate title="Mayor's Permit" dataItem={`PHP ${get(transactionInfo, 'attributes.mayor_permit')}` || '--'} />
                          <InputDisplayTemplate title="Occupational Fee" dataItem={`PHP ${get(transactionInfo, 'attributes.occupational_fee')}` || '--'} />
                          <InputDisplayTemplate title="Tricycle Permit Number Fee" dataItem={`PHP ${get(transactionInfo, 'attributes.tricycle_permit_number_fee')}` || '--'} />
                          <InputDisplayTemplate title="25% Surcharge" dataItem={`PHP ${get(transactionInfo, 'attributes.surcharge')}` || '--'} />
                          <InputDisplayTemplate title="2% Monthly Penalty" dataItem={`PHP ${get(transactionInfo, 'attributes.penalty')}` || '--'} />
                          <InputDisplayTemplate title="Total Amount Paid" dataItem={`PHP ${get(transactionInfo, 'attributes.total_amount')}` || '--'} />
                          <InputDisplayTemplate title="Processing Fee" dataItem={`PHP ${get(transactionInfo, 'attributes.processing_fee', "0.00")}` || '--'} />

                          <InputDisplayTemplate title="Paid Under OR No." dataItem={get(transactionInfo, 'attributes.e_or_number') || '--'} />
                          <InputDisplayTemplate title="Dated" dataItem={get(transactionInfo, 'attributes.e_or_generated_at') || '--'} />
                        </div>
                      </div>
                    </TabPanel>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const InputDisplayTemplate = ({
  title,
  dataItem,
  isImage,
  imgClassName,
  containerClassName,
  isAddress,
  children,
}) => (
  <>
    <div className="row mx-1 py-2 ">
      <div className={`${containerClassName} col-6`}>
        <div className="span-title2 px-2 font-weight-bold">{title}</div>
      </div>
      {isAddress ? (
        <div className={`${containerClassName} col-6`}>
          <div className="text-uppercase span-input2">{children || "N/A"}</div>
        </div>
      ) : (
        <div className={`${containerClassName} col-6`}>
          {!isImage ? (
            <div className="text-uppercase span-input2">
              {dataItem || "N/A"}
            </div>
          ) : (
            <img className={`${imgClassName}`} src={dataItem} alt="" />
          )}
        </div>
      )}
    </div>
  </>
);

InputDisplayTemplate.defaultProps = {
  dataItem: "",
  title: "",
  isImage: false,
  imgClassName: "",
  containerClassName: "",
  isAddress: false,
  children: "",
};

InputDisplayTemplate.propTypes = {
  dataItem: PropTypes.node,
  title: PropTypes.string,
  isImage: PropTypes.bool,
  imgClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  isAddress: PropTypes.bool,
  children: PropTypes.node,
};


ApplicationContent.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  taxpayer: PropTypes.instanceOf(Object).isRequired,
  showTransaction: PropTypes.instanceOf(Function).isRequired,
  transactionInfo: PropTypes.instanceOf(Object).isRequired,
  clearTransaction: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  toggleAssessment: PropTypes.instanceOf(Function).isRequired,
  tagVehicleUnit: PropTypes.instanceOf(Function).isRequired,
  assisting: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ api }) => ({
  assisting: api.loading.indexOf(c.TOGGLE_ASSESSMENT) > -1,
  transactionInfo: get(api, `${c.SHOW_TRANSACTION}.item`) || {},
  taxpayer:
    { ...(get(api, `${c.SHOW_TRANSACTION}.item.included.tax_payer.attributes`) || {}), } || {},
  isLoading: api.loading.indexOf(c.SHOW_TRANSACTION) > -1,
});

const enhance = flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(ApplicationContent);
